"use client"

import { Progress } from "@/components/ui/progress"
import { CheckCircle2, XCircle, Loader } from "lucide-react"

/**
 * Props interface for the ImportSubscribersProgress component
 */
interface ImportSubscribersProgressProps {
  /** Current progress percentage (0-100) */
  progress: number
  /** Total number of emails being processed */
  totalEmails?: number
  /** Number of emails successfully imported */
  successfulImports?: number
  /** Number of emails skipped (already exist) */
  skippedEmails?: number
  /** Number of emails that failed to import */
  failedEmails?: number
  /** Name of the segment emails are being imported into */
  segmentName?: string
  /** Whether the import process has completed */
  importCompleted?: boolean
}

/**
 * Component to display subscriber import progress and results
 *
 * Shows a progress bar during import and statistics about successful, skipped,
 * and failed email imports. After completion, displays a summary of results.
 */
export function ImportSubscribersProgress({
  progress,
  totalEmails = 0,
  successfulImports = 0,
  skippedEmails = 0,
  failedEmails = 0,
  segmentName,
  importCompleted = false,
}: ImportSubscribersProgressProps) {
  const progressPercentage = progress || 0

  return (
    <div className="space-y-4">
      {/* Header section with title and status */}
      <div className="space-y-2">
        <h3 className="text-base font-medium">
          {importCompleted ? "Import summary" : "Import progress"}
        </h3>

        <div className="space-y-2">
          {/* Status indicator with icon */}
          <div className="flex items-center gap-2">
            {importCompleted ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <Loader className="h-4 w-4 animate-spin text-primary" />
            )}
            <span className="text-sm font-medium">
              {importCompleted
                ? segmentName
                  ? `Subscribers imported into segment "${segmentName}"`
                  : "Subscribers imported into your publication"
                : segmentName
                  ? `Importing subscribers into segment "${segmentName}"`
                  : "Importing subscribers into your publication"}
            </span>
          </div>

          {/* Progress bar (only shown during import) */}
          {!importCompleted && (
            <div className="flex items-center w-3/4">
              <div className="flex-1">
                <Progress value={progressPercentage} className="h-2" />
              </div>
              <span className="text-sm font-medium ml-2">
                {Math.round(progressPercentage)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Statistics section - only shown when there's data */}
      {(totalEmails > 0 ||
        successfulImports > 0 ||
        skippedEmails > 0 ||
        failedEmails > 0) && (
        <>
          {/* Divider between header and details */}
          <div className="h-px bg-border my-3" />

          <div className="space-y-2">
            {/* Successful imports count */}
            {successfulImports > 0 && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm">
                  {successfulImports} email{successfulImports !== 1 ? "s" : ""}{" "}
                  imported successfully
                </span>
              </div>
            )}

            {/* Skipped emails count */}
            {skippedEmails > 0 && (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-amber-500" />
                <span className="text-sm">
                  {skippedEmails} email{skippedEmails !== 1 ? "s" : ""} skipped
                  (already {skippedEmails === 1 ? "exists" : "exist"})
                </span>
              </div>
            )}

            {/* Failed imports count */}
            {failedEmails > 0 && (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="text-sm">
                  {failedEmails} email{failedEmails !== 1 ? "s" : ""} failed to
                  import
                </span>
              </div>
            )}

            {/* Remaining emails count */}
            {totalEmails - successfulImports - skippedEmails - failedEmails >
              0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {totalEmails -
                    successfulImports -
                    skippedEmails -
                    failedEmails}{" "}
                  email
                  {totalEmails -
                    successfulImports -
                    skippedEmails -
                    failedEmails !==
                  1
                    ? "s"
                    : ""}{" "}
                  remaining
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Helper text - only shown during import */}
      {!importCompleted && (
        <p className="text-xs text-muted-foreground italic">
          This process may take a few minutes for large files. Please don't
          close this window.
        </p>
      )}
    </div>
  )
}
